<template>
    <popup-modal
        :title="popupTitle"
        custom-classes="w-[904px]"
        @close="$emit('close')"
    >
        <loading v-if="fetchingInterims || fetchingJobs"/>
        <div v-else>
            <div v-if="!selectingJobs" class="grid grid-cols-5 gap-40 min-h-200">
                <div class="col-span-3">
                    <h2 class="text-heading-1 mb-16">
                        {{ $t(`share_job.external.external_partners.popup.title`) }}
                    </h2>
                    <p
                        v-html="$t('share_job.external.external_partners.popup.description')"
                        class="text-body-sm"
                    ></p>
                </div>
                <div v-if="card" class="col-span-2 rounded py-32 bg-white">
                    <div
                        class="w-full h-full bg-no-repeat bg-center"
                        :style="{ backgroundImage: 'url(' + card.image_url + ')', backgroundSize: 'contain' }"
                    ></div>
                </div>
            </div>

            <div v-if="!selectingJobs && card" class="border-t my-40"></div>

            <div v-if="step === 1">

                <h3 class="font-semibold text-body mb-16">
                    {{ $t('interims.published.title') }}
                </h3>

                <div>
                    <segmented-button
                        class="flex-none mb-12"
                        id="published"
                        :options="getSegmentedButtonOptions()"
                        v-model="form.published"
                    />
                    <div v-show="form.published === 1"
                         class="pt-32 px-24 rounded border border-primary-20 bg-primary-5 overflow-hidden">
                        <div class="mb-32">
                            <toggle v-model="setManual">
                                <span class="leading-loose">{{ $t('jobs.create.headers.start_and_end_date_help') }}</span>
                            </toggle>

                            <div v-show="setManual" class="pt-16 flex items-center">
                                <div class="w-1/2 mr-32">
                                    <date-picker white-background v-model="form.start_date">
                                        {{ $t('jobs.create.start_date') }}
                                    </date-picker>
                                </div>
                                <div class="w-1/2">
                                    <date-picker white-background v-model="form.end_date">
                                        {{ $t('jobs.create.end_date') }}
                                    </date-picker>
                                </div>
                            </div>
                        </div>
                    </div>
                    <banner
                        v-show="form.published === 0 && job && !job.is_published"
                        :description="$t('interims.published.info', {type: $t('interims.published.always_active')})"
                        inline
                        info
                    />
                </div>


                <div v-if="selectingJobs" class="flex items-center justify-between">
                    <h3
                        class="font-semibold text-body border-t border-primary-5 pt-24 mb-20"
                        v-html="$t('interims.select_jobs_to_share')"
                    ></h3>
                </div>
                <h3 class="font-semibold text-body border-t border-primary-5 pt-24 mb-20"
                    v-html="$t('interims.select_interims_to_share_with')" v-else></h3>
                <div class="-space-y-px">
                    <template v-if="!selectingJobs">
                        <div class="mb-24">
                            <div v-for="(section) in sections.filter(section => section.interims?.length)"
                                 :key="section.id"
                                 class="mb-12 -space-y-px">

                                <h4 class="flex items-center text-body font-semibold mb-6">
                                    {{ section.name }}
                                </h4>

                                <div v-for="(interim, index) in section.interims">
                                    <share-job-with-interim-list
                                        :key="interim.id"
                                        :class="[
                                            section.interims.length === 1 ? 'rounded-t rounded-b' : !index ? 'rounded-t' : (index === section.interims.length -1 ? 'rounded-b' : ''),
                                            interim.contact_mail ? (form.interimIds.includes(interim.id) ? 'border-secondary-80 bg-secondary-5 relative z-10' : 'border-primary-20 hover:bg-primary-5') : 'border-primary-20 hover:border-semantic-error relative z-10',
                                            interim.contact_mail ? 'cursor-pointer' : 'cursor-not-allowed'
                                        ]"
                                        :form="form"
                                        :interim="interim"
                                        :visible="interim.section_id === section.id"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Interims that don't have section_id -->
                        <div v-for="(interim, index) in interimsWithoutSection">
                            <share-job-with-interim-list :key="interim.id"
                                                         :class="[interimsWithoutSection.length === 1 ? 'rounded-t rounded-b' : !index ? 'rounded-t' : (index === interimsWithoutSection.length -1 ? 'rounded-b' : ''),
                            interim.contact_mail ? (form.interimIds.includes(interim.id) ? 'border-secondary-80 bg-secondary-5 relative z-10' : 'border-primary-20 hover:bg-primary-5') : 'border-primary-20 hover:border-semantic-error relative z-10',
                            interim.contact_mail ? 'cursor-pointer' : 'cursor-not-allowed']"
                                                         :form="form"
                                                         :interim="interim"
                                                         :visible="interim.section_id === null"
                            >
                            </share-job-with-interim-list>
                        </div>
                    </template>
                    <template v-else>
                        <div class="border border-primary-20 rounded max-h-320 overflow-y-scroll" v-if="jobs.length">
                            <div
                                v-for="(job, index) in jobs" :key="job.id"
                                class="px-20 py-6"
                                :class="index !== jobs.length - 1 ? 'border-b border-primary-20' : ''"
                            >
                                <checkbox
                                    v-model="form.jobIds"
                                    :value="job.id"
                                    slot-before
                                    span-class="flex-grow"
                                    classes="items-center justify-end"
                                >
                                    <div class="flex items-center justify-between">
                                        <p class="text-body" :class="!job.published? 'text-primary-40' : ''">
                                            <span class="font-semibold">
                                                {{ translated(job.title, locale, true) }}
                                            </span>
                                            <span v-if="job.active_location.city" class="text-primary-40">
                                                - {{ job.active_location.city }}
                                            </span>
                                        </p>
                                        <tag :display="job.published ? 'success' : 'danger'" no-icon>
                                            <span class="first-letter:uppercase">
                                                {{ job.published ? $t('jobs.published') : $t('jobs.unpublished')}}
                                            </span>
                                        </tag>
                                    </div>
                                </checkbox>
                            </div>
                        </div>
                        <empty-state
                            v-else
                            :description="$t('jobs.empty_state.description')"
                            :show-button="false"
                            :title="$t('jobs.empty_state.title')"
                        />

                    </template>

                    <div class="flex justify-end pt-32">
                        <!-- Couldn't find a way to check on published to-->
                        <simple-button
                            :loading="sending"
                            :disabled="selectingJobs && !(form.jobIds.length || originallySelected.length)"
                            display="secondary"
                            look="primary"
                            @click="goToStep2"
                        >
                            {{ form.interimIds.length || form.jobIds.length ? $t('jobs.next_step') : $t('general.save') }}
                        </simple-button>
                    </div>
                </div>
            </div>

            <div v-if="step === 2">
                <form-editor-interim :confirm-button="((selectedJobsNoInterims && selectedJobsNoInterims.length) || (selectedInterimsNoJobs && selectedInterimsNoJobs.length) ? $t('jobs.save_and_next') : $t('settings.save'))"
                                     :interim="interim"
                                     :job="job"
                                     :selecting-jobs="selectingJobs"
                                     :loading="sending"
                                     :selected-jobs="form.jobIds"
                                     @step="($event === 1 ? step = 1 : ((selectedJobsNoInterims && selectedJobsNoInterims.length) || (selectedInterimsNoJobs && selectedInterimsNoJobs.length)) ? step = $event : send())"
                ></form-editor-interim>
            </div>

            <div v-if="step === 3">
                <p class="text-body font-semibold mb-16">
                    {{ $t('general.send_message') }}
                </p>
                <p class="text-body-sm mb-16">{{ $t('interims.general_message_description') }}</p>
                <p class="text-body-sm mb-16 inline-flex items-center gap-8" v-if="!selectingJobs">
                    <span class="font-semibold">{{ $t('interims.mail_to') }}:</span>
                    <tag
                        :key="'interim-' + interim.id"
                        v-for="interim in selectedInterimsNoJobs"
                    >
                        {{ interim.name }}
                    </tag>
                </p>
                <select-dropdown
                    static-id="emailTemplateNameDropdown"
                    v-model="selectedTemplate"
                    :auto-translate="false"
                    :options="templateChoices"
                    no-auto-sort
                    class="mb-16"
                    searchable
                    @change="hydrateFromTemplate"
                >
                    {{ $t('mailer.choose_template') }}

                    <tabs v-if="languages"
                          :active-key="(templateLocale ? templateLocale : active_locale)"
                          :items="languages"
                          class="w-full inline-flex"
                          scroll
                          @set-active="hydrateFromTemplate"
                    >
                    </tabs>
                </select-dropdown>
                <simple-input v-model="form.subject"
                              :required="true"
                              class="mb-16">
                    {{ $t('mailer.subject') }}
                </simple-input>
                <wysiwyg
                    ref="content"
                    v-model="form.message"
                    :placeholders="['company', selectingJobs ? null : 'job'].filter(i => i)"
                    class="mb-16"
                    with-links
                >
                    {{ $t('mailer.description') }}
                    <template #after>
                        <div
                            :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                            class="flex p-16 border-t border-primary-20"
                        >
                            <div class="text-body-sm flex-grow">
                                <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                            </div>
                            <select-dropdown
                                v-model="selectedSignature"
                                :auto-translate="false"
                                :options="signatureChoices"
                                class="min-w-240"
                            ></select-dropdown>
                        </div>
                    </template>
                </wysiwyg>
                <file-upload ref="fileUpload" v-model="form.attachments" multiple @removed="removeFile">
                    <!--show exsisting files-->
                    <ul
                        class="rounded-6 overflow-hidden w-full mb-16">
                        <li
                            v-for="attachment in form.attachments"
                            v-show="attachment.locale === active_locale"
                            :key="'attachment-' + attachment.id"
                            class="odd:bg-primary-5 relative"
                        >
                            <a
                                :download="!extensions.includes(attachment.extension) ? attachment.original_name : null"
                                :href="attachment.url"
                                :target="extensions.includes(attachment.extension) ? '_blank' : null"
                                class="group flex items-center p-16 cursor-pointer">
                                <div class="mr-auto font-semibold group-hover:text-primary trans-ease">{{ attachment.original_name }}</div>
                                <div class="pl-16">
                                    <div class="h-32 w-32 flex items-center justify-center rounded-6 text-primary-80 trans-ease group-hover:text-primary mr-32">
                                        <svg-icon class="max-w-16 max-h-16" type="download"></svg-icon>
                                    </div>
                                </div>
                            </a>

                            <div class="absolute right-0 inset-y-0 mr-16 flex items-center pointer-events-none">
                                <button class="text-primary-80 hover:text-semantic-error cursor-pointer trans-ease pointer-events-auto h-32 w-32 flex items-center justify-center rounded-6"
                                        @click="confirmDeleteAttachment(attachment)">
                                    <svg-icon class="max-w-16 max-h-16" type="delete"></svg-icon>
                                </button>
                            </div>
                        </li>
                    </ul>
                </file-upload>
                <div class="flex justify-end gap-12 pt-32">
                    <simple-button
                        icon="keyboard-arrow-left"
                        icon-position="left"
                        look="text"
                        @click="step = 2"
                    >
                        {{ $t('interims.back_to_selection') }}
                    </simple-button>
                    <simple-button
                        :disabled="form.message.length < 10"
                        display="secondary"
                        :loading="sending"
                        look="primary"
                        @click="send"
                    >
                        {{ $t('interims.submit_step_2_interim') }}
                    </simple-button>
                </div>
            </div>
        </div>
        <popup-modal-confirm
            v-if="deletingAttachment"
            :confirmText="$t('general.delete_subject', {subject: $t('general.attachment')})"
            :message="$t('general.confirm_delete_subject', {subject: deletingAttachment.original_name})"
            :working="performingDeleteAttachmentRequest"
            @close="cancelDeleteAttachment"
            @confirm="deleteAttachment(deletingAttachment)"
        >
        </popup-modal-confirm>
    </popup-modal>
</template>
<script>
import PopupModal from './ui/PopupModal';
import * as interims from '../services/InterimService';
import * as interimJobs from '../services/InterimJobService';
import * as jobs from '../services/JobService';
import Interim from '../models/Interim';
import {normalizeJson, openableFileExtensionsInWebBrowser} from '../../shared/helpers';
import Loading from './ui/Loading';
import Checkbox from './forms/Checkbox';
import Form from '../etc/forms/Form';
import SimpleButton from './ui/SimpleButton';
import SimpleInput from './forms/Input.vue';
import Wysiwyg from './forms/Wysiwyg';
import Job from '../models/Job';
import moment from 'moment';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import _isEqual from 'lodash/isEqual';
import InterimJob from '../models/InterimJob';
import Toggle from './forms/Toggle';
import SvgIcon from './ui/SvgIcon';
import DatePicker from './forms/DatePicker';
import RadioButton from './forms/RadioButton';
import Tooltip from './navigation/Tooltip';
import InterimSection from '../models/InterimSection';
import * as interimSections from '../services/InterimSectionService';
import ShareJobWithInterimList from './ShareJobWithInterimList';
import FormEditorInterim from './ui/FormEditorInterim';
import SelectDropdown from "./forms/SelectDropdown.vue";
import EmailTemplate from "../models/EmailTemplate";
import Tabs from './navigation/Tabs.vue';
import * as signatures from "../services/EmailSignatureService";
import EmailSignature from "../models/EmailSignature";
import * as templates from "../services/EmailTemplateService";
import FileUpload from "./forms/FileUpload.vue";
import * as documents from '../services/DocumentService';
import PopupModalConfirm from "./ui/PopupModalConfirm.vue";
import sendsMails from "../mixins/sendsMails";
import Tag from "./ui/Tag.vue";
import ShareJobCard from "../models/ShareJobCard";
import SegmentedButton from "./ui/SegmentedButton.vue";
import Banner from "./ui/Banner.vue";
import SearchBar from "./navigation/SearchBar.vue";
import Search from "./ui/Search.vue";
import _debounce from "lodash/debounce";
import EmptyState from "./ui/EmptyState.vue";
import {mapState, mapStores} from "pinia";
import {useMainStore} from "../state/store";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useRepo} from "pinia-orm";
import {useNotificationsStore} from "../state/modules/notifications";

export default {
    components: {
        EmptyState,
        Search,
        SearchBar,
        Banner,
        SegmentedButton,
        Tag,
        PopupModalConfirm,
        FileUpload,
        SelectDropdown,
        FormEditorInterim,
        Tabs,
        ShareJobWithInterimList,
        Tooltip,
        RadioButton,
        DatePicker,
        SvgIcon,
        Wysiwyg,
        SimpleButton,
        SimpleInput,
        Checkbox,
        Loading,
        PopupModal,
        Toggle,
    },
    props: {
        card: ShareJobCard,
        job: Job,
        interim: Interim,
        activeItem: {
            type: String,
            default: 'interim'
        }
    },
    mixins: [sendsMails],
    data() {
        return {
            step: 1,
            fetchingInterims: false,
            fetchingJobs: false,
            originallySelected: [],
            interimJob: [],
            errors: [],
            extensions: openableFileExtensionsInWebBrowser,
            deletingAttachment: null,
            performingDeleteAttachmentRequest: false,
            form: null,
            selectedSignature: 0,
            selectedTemplate: 0,
            templateLocale: '',
            editor: null,
            sending: false,
            jobSearch: '',
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompanyUser']),
        ...mapStores(useAuthenticationStore, useNotificationsStore),
        popupTitle() {
            if (this.card) return '';
            return this.selectingJobs ? this.$t('interims.share_jobs_with_interim') : this.$t('interims.share_job_with_interims')
        },
        jobs() {
            return useRepo(Job)
                .with('interimJobs')
                .get()
                .filter(field => field.title[this.locale] && field.title[this.locale].toLowerCase().includes(this.jobSearch.toLowerCase()))
                .sort((a, b) => a.title[this.locale].localeCompare(b.title[this.locale]));
        },
        interims() {
            //Get all the interims
            return useRepo(Interim)
                .with('interimJobs')
                .orderBy('order')
                .get();
        },
        interimsByType() {
            //Get the interims that are not inside section
            return useRepo(Interim)
                .with('interimJobs')
                .orderBy('order')
                .where('type', this.activeItem)
                .get();
        },
        sections() {
            let sections = useRepo(InterimSection)
                .with('interims')
                .orderBy('order')
                .get();

            //Filter out interims that don't match with active type
            sections.forEach((section, index) => {
                section.interims = section.interims.filter(interim => interim.type === this.activeItem);
            });

            return sections;
        },
        interimsWithoutSection() {
            return this.interimsByType.filter(interim => interim.section_id === null);
        },
        selectedInterimsNoJobs() {
            //Check if this is a new job for this interim
            return this.interimsByType.filter(i => this.form.interimIds.includes(i.id) && this.interimJobIsClosed(i));
        },
        selectedJobsNoInterims() {
            return this.jobs.filter(j => this.form.jobIds.includes(j.id) && this.jobInterimIsClosed(j));
        },
        lastMessage() {
            return this.interimJob.message;
        },
        setManual: {
            get: function () {
                //Check if there is a interim start or end date
                if (this.form.start_date || this.form.end_date) {
                    return true;
                }
                return false;
            },
            set: function (value) {
                if (value) {
                    this.form.start_date = moment().utc();
                    this.form.end_date = moment().utc();
                } else {
                    this.form.start_date = null;
                    this.form.end_date = null;
                }
            },
        },
        selectingJobs() {
            return !!this.interim;
        },
        active_locale() {
            if (!this.templateLocale) {
                return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
            }

            return this.templateLocale;
        },
        languages() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: field,
                };
            });

            return languages;
        },
        templates() {
            return useRepo(EmailTemplate)
                .with('attachments')
                .where('type', 'interim')
                .orderBy('order')
                .get();
        },
        templateChoices() {
            return [
                {value: 0, label: '--- ' + this.$t('general.select') + ' ---'},
                ...this.templates.filter(template => {
                    return template.name[this.active_locale]
                }).map(template => ({
                    value: template.id,
                    label: template.name[this.active_locale],
                })),
            ];
        },
        signatures () {
            return useRepo(EmailSignature).orderBy('created_at').get();
        },
        signatureChoices () {
            return [
                { value: 0, label: this.$t('email_signatures.no_signature_selected') },
                ...this.signatures.map(signature => ({
                    value: signature.id,
                    label: signature.name
                })),
            ];
        },
        signaturePreview () {
            return this.signatures.find(signature => signature.id == this.selectedSignature)?.content;
        },
    },
    methods: {
        moment,
        _isEqual,
        handleSearch: _debounce(function (value) {
            this.jobSearch = value;
        }, 500),
        getSegmentedButtonOptions() {
            return [
                {
                    label: this.$t('interims.published.as_long_published'),
                    value: 0,
                },
                {
                    label: this.$t('interims.published.always_active'),
                    value: 1,
                },
            ]
        },
        fetch() {
            if (!this.selectingJobs)
                return this.fetchInterims();
            else
                return this.fetchJobs();
        },
        fetchInterims() {
            this.fetchingInterims = true;

            Promise.all([
                interims.index(this.job.id)
                    .then(result => {
                        return useRepo(Interim).save(normalizeJson(result.data));
                    })
                    .then(() => {
                        this.originallySelected = this.interims
                            .filter(interim => interim.interimJobs.filter(entry => {
                                return entry.closed_at == null && entry.job_id == this.job.id;
                            }).length)
                            .map(interim => interim.id);

                        return this.form.interimIds.push(...this.originallySelected);
                    })
                    .then(() => {
                        this.setFormData();
                    }),

                interimSections.index().then(results => {
                    return useRepo(InterimSection).save(normalizeJson(results.data));
                }),
            ]).then(() => {
                this.fetchingInterims = false;
            });
        },
        fetchJobs() {
            this.fetchingJobs = true;

            jobs.index({ filters: { active: 1 }, extraIncludes: ['interimJobs'], all: 1 })
                .then(result => {
                    return useRepo(Job).save(normalizeJson(result.data));
                })
                .then(() => {
                    this.originallySelected = this.jobs
                        .filter(job => job.interimJobs.find(entry => {
                            return entry.closed_at == null && entry.interim_id === this.interim.id;
                        }))
                        .map(job => job.id);

                    return this.form.jobIds.push(...this.originallySelected);
                })
                .then(() => {
                    return this.setFormData();
                })
                .then(() => {
                    this.fetchingJobs = false;
                });
        },
        fetchTemplates() {
            signatures.index().then(result => {
                return useRepo(EmailSignature).save(normalizeJson(result.data));
            }).then(() => {
                this.selectedSignature = this.authenticationStore.defaultSignature?.id || 0;
            });

            templates.index().then(result => {
                return useRepo(EmailTemplate).save(normalizeJson(result.data));
            }).then(() => {
                this.fetchingTemplates = false;
            });
        },
        setFormData() {
            //Get the interim_job data
            const interimJob = useRepo(InterimJob).where('job_id', this.job?.id).first()
                || this.interim?.interimJobs.find(interimJob => interimJob.closed_at === null && !!interimJob.published)

            if (interimJob) {
                this.interimJob = interimJob;

                this.form.published = interimJob.published;
                this.form.start_date = interimJob.start_date
                this.form.end_date = interimJob.end_date;

                this.form.message = this.lastMessage;
            }

            return this.form;
        },
        goToStep2() {
            if (this.form.interimIds.length || this.form.jobIds.length)
                return this.step = 2;

            this.send();
        },
        send() {
            this.sending = true;

            //set published as 1 or 0
            this.form.data.published = Number(this.form.data.published);

            if (this.signaturePreview) {
                this.form.message = this.form.message + (this.signaturePreview ? ('<br>' + this.signaturePreview) : '');
            }

            this.form.submit(data => {
                if (this.selectingJobs) {
                    return interimJobs.shareJobsWithInterim(this.interim.id, {
                        data: {
                            attributes: data,
                        },
                    });
                } else {
                    return interimJobs.shareJobWithInterims(this.job.id, {
                        data: {
                            attributes: data,
                        },
                    });
                }
            }).then((res) => {
                this.$emit('close', true);

                this.notificationsModuleStore[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t((this.step === 2 ? 'general.changes_saved' : 'interims.job_has_been_shared')),
                });
            }).finally(() => {
                this.sending = false;
            });
        },
        hydrateFromTemplate(data) {
            let lang = isNaN(data) ? data : this.active_locale;
            let templateId = !isNaN(data) ? data : this.selectedTemplate;

            this.templateLocale = lang;

            let template = this.template = this.templates.find(template => template.id === templateId);

            //Always empty attachments
            this.form.attachments = [];

            if (typeof template != 'undefined') {
                //Check for attachments
                if (template.attachments.length > 0) {
                    //Add attachments to form by locale
                    this.form.attachments = template.attachments.filter(obj => obj.locale === lang);
                }

                this.form.subject = template.subject[lang];
                this.$refs.content.setContent(template.content[lang]);
            }
        },
        interimJobIsClosed(interim) {
            const interimJob = interim.interimJobs.find(j => j.job_id === this.job.id)
            if (!interimJob) return true;
            return !!interimJob.closed_at;
        },
        jobInterimIsClosed(interim) {
            const interimJob = interim.interimJobs.find(j => j.interim_id === this.interim.id)
            if (!interimJob) return true;
            return !!interimJob.closed_at;
        },
        removeFile({rawFile}) {
            if (!rawFile.id) return;
            documents.destroy(rawFile.id).then(() => {
                this.form.attachments = this.form.attachments.filter(attachment => attachment.id !== rawFile.id);
            });
        },
        confirmDeleteAttachment(attachment) {
            this.deletingAttachment = attachment;
        },
        cancelDeleteAttachment() {
            this.deletingAttachment = null;
        },
        deleteAttachment(attachment) {
            this.performingDeleteAttachmentRequest = true;

            //Remove from list, not from default template
            this.form.attachments = this.form.attachments.filter(att => att.id !== attachment.id);

            this.deletingAttachment = null;
            this.performingDeleteAttachmentRequest = false;
        },
        generateInterimForm() {
            const fields = [
                {name: 'interimIds', value: []},
                {name: 'jobIds', value: []},
                {name: 'subject', value: ''},
                {name: 'message', value: ''},
                {name: 'email', value: this.currentUser?.email ?? ''},
                {name: 'phone', value: this.currentCompanyUser?.phone ?? ''},
                {name: 'published', value: 0},
                {
                    name: 'start_date',
                    value: null,
                    accessor: value => value ? moment.utc(value) : null,
                },
                {
                    name: 'end_date',
                    value: null,
                    accessor: value => value ? moment.utc(value) : null,
                },
                {name: 'attachments', value: []},
            ];

            return new Form(fields);
        }
    },
    beforeMount() {
        this.form = this.generateInterimForm();
    },
    mounted() {
        this.fetch();
        this.fetchTemplates();

        this.$nextTick(() => {
            Promise.all(this.documents.map(i => i.toFile()))
                .then(files => {
                    if (this.$refs.fileUpload) {
                        this.$refs.fileUpload.setFiles(files);
                    }
                });
        })
    },
};
</script>
